import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import usePastoralCareData from "layouts/tables/data/pastoralCareData";

function MemberPicker({ search, setSearch, results, onSelect }) {
  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        label="Member"
        placeholder="Search by name or phone..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        margin="normal"
      />
      {results.length > 0 && (
        <Card
          sx={{
            position: "absolute",
            zIndex: 10,
            width: "100%",
            mt: -1,
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {results.map((m) => (
            <Box
              key={m.id}
              onClick={() => onSelect(m)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>{m.first_name?.[0]}</Avatar>
              <Typography variant="body2">
                {m.first_name} {m.surname}
              </Typography>
            </Box>
          ))}
        </Card>
      )}
    </Box>
  );
}
MemberPicker.propTypes = {
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  results: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function PastoralCare() {
  const {
    prayerColumns,
    prayerRows,
    visitColumns,
    visitRows,
    isLoading,
    error,
    handlePrint,
    prayerModalOpen,
    setPrayerModalOpen,
    visitModalOpen,
    setVisitModalOpen,
    prayerForm,
    setPrayerForm,
    visitForm,
    setVisitForm,
    handleOpenPrayerModal,
    handleOpenVisitModal,
    handleSubmitPrayer,
    handleSubmitVisit,
    isSaving,
    memberSearch,
    setMemberSearch,
    memberResults,
    handleSelectPrayerMember,
    handleSelectVisitMember,
  } = usePastoralCareData();

  const [tab, setTab] = useState(0);

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading pastoral care records...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Pastoral Care"
        subtitle="Track prayer requests and pastoral/hospital/home visits."
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={tab === 0 ? handleOpenPrayerModal : handleOpenVisitModal}
            >
              {tab === 0 ? "New Prayer Request" : "Log a Visit"}
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Prayer Requests" />
          <Tab label="Visit Log" />
        </Tabs>
        {tab === 0 ? (
          <DataTable
            table={{ columns: prayerColumns, rows: prayerRows }}
            isSorted
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
            showTotalEntries
            canSearch
            searchLabel="Search prayer requests..."
          />
        ) : (
          <DataTable
            table={{ columns: visitColumns, rows: visitRows }}
            isSorted
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
            showTotalEntries
            canSearch
            searchLabel="Search visits..."
          />
        )}
      </Card>

      {/* New Prayer Request */}
      <FormDialog
        open={prayerModalOpen}
        onClose={() => setPrayerModalOpen(false)}
        title="New Prayer Request"
        onSave={handleSubmitPrayer}
        saveLabel={isSaving ? "Saving..." : "Save"}
        saveDisabled={!prayerForm.request.trim() || isSaving}
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <MemberPicker
            search={memberSearch}
            setSearch={(v) => {
              setMemberSearch(v);
              setPrayerForm((prev) => ({ ...prev, member_id: null }));
            }}
            results={memberResults}
            onSelect={handleSelectPrayerMember}
          />
          {!prayerForm.member_id && (
            <TextField
              label="Or requester name (non-member / visitor)"
              value={prayerForm.requester_name}
              onChange={(e) => setPrayerForm({ ...prayerForm, requester_name: e.target.value })}
              fullWidth
              margin="normal"
            />
          )}
          <TextField
            label="Prayer request"
            value={prayerForm.request}
            onChange={(e) => setPrayerForm({ ...prayerForm, request: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={3}
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={prayerForm.confidential}
                onChange={(e) => setPrayerForm({ ...prayerForm, confidential: e.target.checked })}
              />
            }
            label="Confidential (only visible to admin/pastoral staff)"
          />
        </FormSection>
      </FormDialog>

      {/* Log a Visit */}
      <FormDialog
        open={visitModalOpen}
        onClose={() => setVisitModalOpen(false)}
        title="Log a Pastoral Visit"
        onSave={handleSubmitVisit}
        saveLabel={isSaving ? "Saving..." : "Save"}
        saveDisabled={!visitForm.member_id || !visitForm.visit_date || isSaving}
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <MemberPicker
            search={memberSearch}
            setSearch={(v) => {
              setMemberSearch(v);
              setVisitForm((prev) => ({ ...prev, member_id: null }));
            }}
            results={memberResults}
            onSelect={handleSelectVisitMember}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Visit Date"
              type="date"
              value={visitForm.visit_date}
              onChange={(e) => setVisitForm({ ...visitForm, visit_date: e.target.value })}
              fullWidth
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Type"
              value={visitForm.type}
              onChange={(e) => setVisitForm({ ...visitForm, type: e.target.value })}
              fullWidth
              margin="normal"
            >
              <MenuItem value="home">Home</MenuItem>
              <MenuItem value="hospital">Hospital</MenuItem>
              <MenuItem value="counseling">Counseling</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Box>
          <TextField
            label="Notes"
            value={visitForm.notes}
            onChange={(e) => setVisitForm({ ...visitForm, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={3}
          />
          <FormControlLabel
            control={
              <Switch
                checked={visitForm.confidential}
                onChange={(e) => setVisitForm({ ...visitForm, confidential: e.target.checked })}
              />
            }
            label="Confidential (only visible to admin/pastoral staff)"
          />
        </FormSection>
      </FormDialog>
    </Box>
  );
}

export default PastoralCare;
